import { Injectable } from '@angular/core'
import {
  HttpService,
  IAdvancedTableInfo,
  IContactsGeoLocationsResponse,
  IExportFileResponse,
  INew_TableAction,
  IPaginationFilteredRequest,
  ITimePeriodRequest,
  TableActionsService,
  TableFilterService
} from '@lla-platform/core/core-data-access'
import { DateService } from '@lla-platform/core/core-util'
import { Observable } from 'rxjs'
import {
  IArInvoicesListResponse,
  IInvoicesListOverviewResponse
} from '../interfaces/ar-invoices-list.interface'
import { IArInvoiceRequest, IArInvoicesResponse } from '../interfaces/ar-invoices.interface'
import {
  IReceivableDetailedResponse,
  IUpdateReceivableRequest
} from '../interfaces/receivable.interface'
import {
  IReceivablesSummaryByShopRequest,
  IShopReceivableResponse
} from '../interfaces/shops-receivable.interface'
import { RECEIVABLE_API_URL } from './receivable-api-routes'
import {
  IInvoiceLinesListResponse,
  IInvoiceLinesOverviewResponse,
  IPriceAdherenceRequest,
  IPriceAdherenceResponse
} from '../interfaces/invoice-lines-report.interface'
import {
  ICategoryOverviewRequest,
  ICategoryOverviewResponse
} from '../interfaces/category-overview.interface'
import { IInvoiceDetailResponse } from '../interfaces/invoice.interface'
import {
  IInvoiceGrossProfitRequest,
  IInvoiceGrossProfitResponse
} from '../interfaces/invoice-gross-profit.interface'
import { IGrossProfitListResponse } from '../interfaces/gross-profit-list.interface'
import { IGpPackageListResponse } from '../interfaces/gp-package-list.interface'
import {
  IGpPackageListRequest,
  IInvoiceGpPackageResponse
} from '../interfaces/invoice-gp-package.interface'
import {
  IPackageUnperformedRequest,
  IPackageUnperformedResponse
} from '../interfaces/package-unperformed.interface'
import { IInvoicePackageLinesResponse } from '../interfaces/invoice-package-lines.interface'
import {
  IReceivableSnapshotRequest,
  IReceivableSnapshotResponse
} from '../interfaces/receivable-snapshot.interface'

@Injectable({
  providedIn: 'root'
})
export class ReceivableService {
  constructor(
    private http: HttpService,
    private dateService: DateService,
    private tableFilterService: TableFilterService,
    private tableActionsService: TableActionsService
  ) {}

  getDetailedList(info: INew_TableAction): Observable<IReceivableDetailedResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.detailedList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  exportReceivablesList(info: INew_TableAction): Observable<IExportFileResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.exportReceivablesList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getReceivablesSummaryByShop(
    payload: IReceivablesSummaryByShopRequest
  ): Observable<IShopReceivableResponse> {
    return this.http.post(RECEIVABLE_API_URL.receivablesSummaryByShop, payload)
  }

  updateReceivable(receivableId: string, payload: IUpdateReceivableRequest) {
    return this.http.put(RECEIVABLE_API_URL.base(receivableId), payload)
  }

  exportReceivablesSummaryByShop(
    payload: IReceivablesSummaryByShopRequest
  ): Observable<IExportFileResponse> {
    return this.http.post(RECEIVABLE_API_URL.exportReceivablesSummaryByShop, payload)
  }

  getArInvoicesSummary(payload: IArInvoiceRequest): Observable<IArInvoicesResponse> {
    const { from, to } = payload
    return this.http.post(RECEIVABLE_API_URL.arInvoicesSummary, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  exportArInvoicesSummary(payload: ITimePeriodRequest): Observable<IExportFileResponse> {
    const { from, to } = payload
    return this.http.post(RECEIVABLE_API_URL.exportArInvoicesSummary, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getInvoicesListOverview(info: INew_TableAction): Observable<IInvoicesListOverviewResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.invoicesListOverview,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getArInvoicesList(info: INew_TableAction): Observable<IArInvoicesListResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.arInvoicesList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  exportArInvoicesList(info: INew_TableAction): Observable<IExportFileResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.exportArInvoicesList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getInvoicesGeoLocations(info: INew_TableAction): Observable<IContactsGeoLocationsResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.invoicesGeoLocations,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getGrossProfitList(info: INew_TableAction): Observable<IGrossProfitListResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.grossProfitList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  exportGrossProfitList(info: INew_TableAction): Observable<IExportFileResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.exportGrossProfitList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getGrossProfit(payload: IInvoiceGrossProfitRequest): Observable<IInvoiceGrossProfitResponse> {
    const { from, to } = payload
    return this.http.post(RECEIVABLE_API_URL.getGrossProfit, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  exportGrossProfit(payload: IInvoiceGrossProfitRequest): Observable<IExportFileResponse> {
    const { from, to } = payload
    return this.http.post(RECEIVABLE_API_URL.exportGrossProfit, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getGpPackageList(info: INew_TableAction): Observable<IGpPackageListResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.gpPackageList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  exportGpPackageList(info: INew_TableAction): Observable<IExportFileResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.exportGpPackageList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getCategoryOverview(payload: ICategoryOverviewRequest): Observable<ICategoryOverviewResponse> {
    const { from, to } = payload
    return this.http.post(RECEIVABLE_API_URL.categoryOverview, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getGpPackage(payload: IGpPackageListRequest): Observable<IInvoiceGpPackageResponse> {
    const { from, to } = payload
    return this.http.post(RECEIVABLE_API_URL.getGpPackage, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  exportGpPackage(payload: IGpPackageListRequest): Observable<IExportFileResponse> {
    const { from, to } = payload
    return this.http.post(RECEIVABLE_API_URL.exportGpPackage, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getReceivablesSummaryByAccount(
    payload: IReceivablesSummaryByShopRequest
  ): Observable<IShopReceivableResponse> {
    return this.http.post(RECEIVABLE_API_URL.receivablesSummaryByAccount, payload)
  }

  exportReceivablesSummaryByAccount(
    payload: IReceivablesSummaryByShopRequest
  ): Observable<IExportFileResponse> {
    return this.http.post(RECEIVABLE_API_URL.exportReceivablesSummaryByAccount, payload)
  }

  getInvoice(invoiceId: string): Observable<IInvoiceDetailResponse> {
    return this.http.get(RECEIVABLE_API_URL.getInvoice(invoiceId))
  }

  getArPaymentsSummary(payload: IArInvoiceRequest): Observable<IArInvoicesResponse> {
    const { from, to } = payload
    return this.http.post(RECEIVABLE_API_URL.arPaymentsSummary, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  exportArPaymentsSummary(payload: IArInvoiceRequest): Observable<IExportFileResponse> {
    const { from, to } = payload
    return this.http.post(RECEIVABLE_API_URL.exportArPaymentsSummary, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getPackageUnperformed(payload: IPackageUnperformedRequest): Observable<IPackageUnperformedResponse> {
    return this.http.post(RECEIVABLE_API_URL.getPackageUnperformed, this.packageUnperformedBody(payload))
  }

  exportPackageUnperformed(payload: IPackageUnperformedRequest): Observable<IExportFileResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.exportPackageUnperformed,
      this.packageUnperformedBody(payload)
    )
  }

  private packageUnperformedBody(payload: IPackageUnperformedRequest) {
    const { from, to, locationIds, page, sortState } = payload
    return {
      ...this.tableFilterService.createBaseRequestPayload({ page, sortState }),
      locationIds,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    }
  }

  getInvoicePackageLinesList(info: INew_TableAction): Observable<IInvoicePackageLinesResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.invoicePackageLinesList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  exportInvoicePackageLinesList(info: INew_TableAction): Observable<IExportFileResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.exportInvoicePackageLinesList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getInvoiceLinesOverview(info: INew_TableAction): Observable<IInvoiceLinesOverviewResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.invoiceLinesOverview,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getInvoiceLinesPriceAdherence(payload: IPriceAdherenceRequest): Observable<IPriceAdherenceResponse> {
    return this.http.post(RECEIVABLE_API_URL.invoiceLinesPriceAdherence, {
      ...this.tableActionsService.createRequestPayload(payload.info),
      groupBy: payload.groupBy
    })
  }

  getInvoiceLinesList(info: INew_TableAction): Observable<IInvoiceLinesListResponse> {
    return this.http.post(
      RECEIVABLE_API_URL.invoiceLinesList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getReceivableSnapshot(payload: IReceivableSnapshotRequest): Observable<IReceivableSnapshotResponse> {
    return this.http.post(RECEIVABLE_API_URL.receivableSnapshot, payload)
  }

  exportReceivableSnapshot(payload: IReceivableSnapshotRequest): Observable<IExportFileResponse> {
    return this.http.post(RECEIVABLE_API_URL.exportReceivableSnapshot, payload)
  }
}
